import { SerializedStyles, css } from "@emotion/react";
import styled from "@emotion/styled";
import { CardElement, CardElementChangeEvent } from "@recurly/react-recurly";
import { FC, useState } from "react";
import { Field, useForm } from "react-final-form";
import { useSelector } from "react-redux";

import { DesignToken, FormField, RadiusToken } from "@livechat/design-system-react-components";

import { ResponsiveBreakpoint } from "constants/responsive-breakpoint";
import { selectIsDarkMode } from "store/features/app";
import { withPx, withVar } from "themes/helpers";

import { ICardFormValues, getCardFormFieldError, hasCardFormFieldError, requiredCardFormFieldsErrors } from "./helpers";

export const CardInputField: FC = () => {
  const [isEmpty, setIsEmpty] = useState(true);
  const [isValid, setIsValid] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  const form = useForm<ICardFormValues>();
  const isDarkModeTheme = useSelector(selectIsDarkMode);

  const setErrorMessage = (message: string | null): void => {
    form.change("creditCardError", message);
  };

  const handleChange = (event: CardElementChangeEvent): void => {
    const { empty, valid } = event;

    setIsEmpty(empty);
    setIsValid(valid);

    if (!empty) setErrorMessage(null);
  };

  const handleBlur = (): void => {
    setIsFocused(false);
    form.mutators.setFieldTouched("creditCardError", true);

    if (isEmpty) setErrorMessage(requiredCardFormFieldsErrors.creditCardError ?? null);
    else if (!isValid) setErrorMessage("Please correct card details");
    else setErrorMessage(null);
  };

  const handleFocus = (): void => {
    setIsFocused(true);
  };

  return (
    <Field
      name="creditCardError"
      validate={(value?: string | null) => (value ? value : undefined)}
      render={({ input, meta }) => (
        <FullWidthFormField
          labelText="Credit card"
          labelFor={input.name}
          error={!isFocused ? getCardFormFieldError(meta) : undefined}
        >
          <StyledCardElement
            id={input.name}
            error={!isFocused && hasCardFormFieldError(meta)}
            focused={isFocused}
            onChange={handleChange}
            onFocus={handleFocus}
            onBlur={handleBlur}
            style={{
              placeholder: { color: "#8D8D95" }, // DesignToken.ContentBasicDisabled
              invalid: { fontColor: isDarkModeTheme ? "#ff8282" : "#ca0005" }, // DesignToken.ActionNegativeDefault
              fontSize: "14px",
              fontColor: isDarkModeTheme ? "#f6f6f7" : "#131317", // DesignToken.ContentBasicPrimary
            }}
          />
        </FullWidthFormField>
      )}
    />
  );
};

const FullWidthFormField = styled(FormField)`
  width: 100%;
`;

const hoverInputStyle = css`
  &:hover {
    border: 1px solid ${withVar(DesignToken.BorderBasicHover)};
  }
`;
const focusedInputStyle = css`
  border: 1px solid ${withVar(DesignToken.ActionPrimaryDefault)};
`;
const errorInputStyle = css`
  border: 1px solid ${withVar(DesignToken.BorderBasicNegative)};
`;

const StyledCardElement = styled(CardElement)<{ error: boolean; focused: boolean }>`
  background: ${withVar(DesignToken.SurfacePrimaryDefault)};
  height: 36px;
  border: 1px solid ${withVar(DesignToken.BorderBasicPrimary)};
  border-radius: ${withVar(RadiusToken.Radius3)};

  & > div {
    height: 36px;
  }

  @media screen and (max-width: ${withPx(ResponsiveBreakpoint.Mobile)}) {
    height: 42px;

    & > div {
      height: 42px;
    }
  }

  ${(p): SerializedStyles | string => (!p.focused && !p.error ? hoverInputStyle : "")}
  ${(p): SerializedStyles | string => (p.focused && !p.error ? focusedInputStyle : "")}
  ${(p): SerializedStyles | string => (p.error ? errorInputStyle : "")}
`;
